import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Bundle the pdf.js worker locally (Vite ?url asset) — a CDN worker can be blocked
// by CSP/CORS and silently fail. Same pattern as create-contract.page.tsx.
// @ts-ignore — Vite resolves the ?url suffix to the built asset URL at build time.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { InvoicePreview } from './invoice/invoice-preview.component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useRedux';
import { useSelector } from 'react-redux';
import SendInvoiceConfirmationModal from './send-invoice-confirmation-modal.component';
import { formatAmountWithLocale } from '../helper/common.helper';
import { LatestDocumentStatus } from '../features/bankid/bankid.slice';
import { ARTICLE_NUMBER_TO_DEDUCTION_TYPE, TAX_FREE_ARTICLE_CONFIG } from '../constants/invoice-deductions.constants';
import { hasCompleteBankInfo } from '../features/bank/bank.helpers';
import apiCall from '../../api';
import BankInfoModal from './bank-info-modal.component';
import CompleteProfileModal from './modal/onboarding/complete-profile-modal.component';
import { flagInvalidDescription } from '../utils/descriptionValidation.utils';
import './invoice-preview-modal.scss';

// pdf.js worker — served from our own bundle, version-matched to react-pdf's pdfjs.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface InvoicePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName?: string;
  logoUrl?: string;
  invoiceNumber?: string;
  totalAmount?: string;
  calculatedSubAmount?: any;
  isEditMode?: boolean;
  onSendInvoice?: () => void;
  isLoading?: boolean;
  // Edit mode only: the Edit Request button stays disabled until the user changes
  // something in the invoice tool. Ignored when not in edit mode.
  isEditRequestEnabled?: boolean;
  invoiceDay?: string;
  dueDay?: string;
  dueDate?: string;
  selectedCustomer?: any;
  deductionsByFreelancer?: any;
  copiedInvoiceData?: any;
  onBankIdVerificationRequired?: () => void;
  dataRef?: React.MutableRefObject<any>;
  validateServiceDescriptionsRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  validateDatesRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  freelancerSubtotals?: Array<{ name: string; subtotal: string }>;
}

export const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
  isOpen,
  onClose,
  brandName,
  logoUrl,
  invoiceNumber = 'INV_25082025_001',
  totalAmount = 'XXXXX,XX SEK',
  calculatedSubAmount,
  isEditMode,
  onSendInvoice,
  isLoading = false,
  isEditRequestEnabled = false,
  invoiceDay = '',
  dueDay = '',
  dueDate = '',
  selectedCustomer,
  deductionsByFreelancer = {},
  copiedInvoiceData,
  onBankIdVerificationRequired,
  dataRef,
  validateServiceDescriptionsRef,
  validateDatesRef,
  freelancerSubtotals = [],
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { preliminaryInvoice, preliminaryLoading } = useAppSelector((state: any) => state?.invoice);
  const isIdentityVerified = useSelector((state: any) => state?.bankidAuthFlow?.isIdentityVerified);
  const latestDocumentStatus = useSelector((state: any) => state?.bankidAuthFlow?.latestDocumentStatus);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBankInfoModal, setShowBankInfoModal] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [initialProfileCountry, setInitialProfileCountry] = useState('');
  // "Added Attachment" button lives in the PDF toolbar; clicking it opens a
  // small modal listing the attached file names (click a name to open it).
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  const allAttachedFiles = useMemo(() => {
    // Extract all files from deductionsByFreelancer. copiedInvoiceData.file /
    // .workingHours items never carry real fileContent bytes (the backend
    // doesn't store attachment bytes — only a blob-storage fileName), so
    // there is nothing to extract from them; only in-session File objects
    // (already attached to a deduction) are ever previewable before save.
    const files: Array<{ name: string; file: File | Blob; deductionType: string }> = [];
    Object.keys(deductionsByFreelancer).forEach((freelancerId) => {
      const deductions = deductionsByFreelancer[freelancerId] || [];
      deductions.forEach((deduction: any) => {
        if (deduction.files && deduction.files.length > 0) {
          deduction.files.forEach((file: File) => {
            files.push({ name: file.name, file, deductionType: deduction.deductionType || 'document' });
          });
        }
      });
    });
    return files;
  }, [deductionsByFreelancer]);

  // ── PDF viewer (react-pdf) — renders every page fit-to-width so the preview
  // opens fully and looks consistent on every screen size, including mobile. ──
  const pdfScrollRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [zoom, setZoom] = useState(1);

  // This preview renders inline in the dashboard's content area (sidebar/nav/
  // footer stay visible), not as a full-screen overlay. A plain height:100vh
  // or height:100% here doesn't reliably account for the Header above it (the
  // dashboard's own layout isn't a simple definite-height chain), so measure
  // the real remaining space with JS — same pattern as the PDF's fit-to-width
  // sizing below. This keeps the viewer's own height exactly bounded, so only
  // its internal PDF scroll area ever scrolls — the toolbar never does.
  const pageRootRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = pageRootRef.current;
    if (!el) return;
    const measureHeight = () => {
      const top = el.getBoundingClientRect().top;
      setAvailableHeight(Math.max(320, window.innerHeight - top));
    };
    measureHeight();
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [isOpen]);

  // The viewer card (toolbar + scroll area) spans the full available width,
  // but the PDF page itself should stay the same size it always has been —
  // only the surrounding card/background widens, not the document.
  const PDF_PAGE_MAX_WIDTH = 916;

  const measurePdf = useCallback(() => {
    const el = pdfScrollRef.current;
    if (el) {
      // clientWidth already excludes the vertical scrollbar; subtract the
      // horizontal padding (18px each side) + a small rounding buffer so the page
      // never overflows its box → no horizontal scrollbar at fit (100%).
      setPageWidth(Math.max(240, Math.min(el.clientWidth - 44, PDF_PAGE_MAX_WIDTH)));
    }
  }, []);

  useEffect(() => {
    const el = pdfScrollRef.current;
    if (!el) return;
    measurePdf();
    const ro = new ResizeObserver(() => measurePdf());
    ro.observe(el);
    // Browser zoom (e.g. 70%/67%) fires `resize`, not always ResizeObserver —
    // listen to both so the PDF always re-fits the full available width.
    window.addEventListener('resize', measurePdf);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measurePdf);
    };
    // re-run once the scroll container actually mounts (pdf ready / loading done)
  }, [measurePdf, preliminaryLoading, preliminaryInvoice]);

  const zoomIn = () => setZoom((z) => Math.min(2.5, +(z + 0.2).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)));
  const resetZoom = () => setZoom(1);

  // Each freshly generated preview starts at fit-to-width (100%).
  useEffect(() => { setZoom(1); }, [preliminaryInvoice]);

  // Extract query parameters for warning condition
  const queryParams = new URLSearchParams(window.location.search);
  const customerIdFromQuery = parseInt(queryParams.get("customerId") || "", 10) || undefined;
  const preId = queryParams.get("preId") || undefined;
  
  const checkBankInfo = async (): Promise<boolean> => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        toast.error(String(t('User data not found') || 'User data not found'));
        return false;
      }
      const user = JSON.parse(userDataString);
      const userId = user?.userData?.userId;
      if (!userId) {
        toast.error(String(t('User ID not found') || 'User ID not found'));
        return false;
      }
      const res = await apiCall.get(`/employee/get/${userId}`);
      const response = res?.data?.response;
      // isBriteIdExist/isBriteSessionAccount live on the top-level response;
      // the rest (bank/account/clearing/iban/bic/bban) are nested under
      // customerSalaryInformation[0] — merge them for hasCompleteBankInfo's
      // same isBriteLinked precedence used across profile settings/onboarding.
      const bankRecord = {
        ...response?.customerSalaryInformation?.[0],
        isBriteIdExist: response?.isBriteIdExist,
        isBriteSessionAccount: response?.isBriteSessionAccount,
      };
      if (!hasCompleteBankInfo(bankRecord)) {
        setShowBankInfoModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error checking bank info:', err);
      toast.error(String(t('Error checking bank information') || 'Error checking bank information'));
      return false;
    }
  };

  const checkCompleteProfile = async (): Promise<boolean> => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) return true;
      const user = JSON.parse(userDataString);
      const userId = user?.userData?.userId;
      if (!userId) return true;
      const res = await apiCall.get(`/signupuserv2/user/${userId}`);
      const userProfile = res?.data?.response?.user;

      /* Same set CompleteProfileModal's validateForm enforces — see the twin
         of this check in invoice-footer.component.tsx. City and country alone
         let an account with no phone number through to a form it could not
         then get past. */
      const filled = (value: any) => typeof value === 'string' && value.trim() !== '';
      const profileIncomplete =
        !filled(userProfile?.city) ||
        !filled(userProfile?.country) ||
        !filled(userProfile?.address) ||
        !filled(userProfile?.zipCode) ||
        !filled(userProfile?.profession) ||
        !filled(userProfile?.phoneNo);

      if (profileIncomplete) {
        setInitialProfileCountry(userProfile?.country || '');
        setShowCompleteProfileModal(true);
        return false;
      }
      return true;
    } catch (err) {
      return true;
    }
  };

  const validateDescription = (): boolean => {
    const invoiceData = dataRef?.current;
    const description = invoiceData?.formData?.otherDetails?.customerNotes || '';
    if (!description || description.trim().length === 0) {
      toast.error(String(t('PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE') || 'Please enter customer notes (description) before creating invoice'));
      flagInvalidDescription();
      return false;
    }
    const descriptionLength = description.trim().length;
    const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    if (descriptionLength < 20 && wordCount < 3) {
      toast.error(String(t('Enter at least 20 characters or 3 words') || 'Enter at least 20 characters or 3 words in customer notes'));
      flagInvalidDescription();
      return false;
    }
    return true;
  };

  const handleSendInvoiceClick = async () => {
    // Amount too low to cover fees/tax: the API would reject the invoice, so stop here.
    if (calculatedSubAmount?.response?.isPayoutNegative) {
      toast.error(String(t("PAYOUT_NEGATIVE_ERROR")));
      return;
    }

    // 0. Check identity verification (skip in edit mode)
    if (!isEditMode && isIdentityVerified === false && latestDocumentStatus !== LatestDocumentStatus.Approved) {
      if (onBankIdVerificationRequired) onBankIdVerificationRequired();
      return;
    }

    setIsValidating(true);

    // 1. Validate working hours: no article row must have amount = 0 or amountPerUnit = 0
    const invoiceDataEarly = dataRef?.current;
    const ALL_VALIDATED_ARTICLE_NUMBERS = new Set([
      '92', '94', '95', '96', '97',
      ...Object.keys(ARTICLE_NUMBER_TO_DEDUCTION_TYPE),
      ...Object.values(TAX_FREE_ARTICLE_CONFIG).map((c) => c.articleNumber),
    ]);
    const serviceTableDataByFreelancer = invoiceDataEarly?.serviceTableDataByFreelancer || {};

    // Require at least ONE working-hour line (only-deduction invoices aren't valid).
    const totalWorkingHours = Object.values(serviceTableDataByFreelancer).reduce(
      (n: number, fd: any) => n + (Array.isArray(fd?.workingHours) ? fd.workingHours.length : 0),
      0
    );
    if (totalWorkingHours === 0) {
      toast.error(String(t('PLEASE_ADD_AT_LEAST_ONE_WORKING_HOUR') || 'Please add at least one working hour before creating the invoice.'));
      setIsValidating(false);
      return;
    }

    const hasZeroAmountRow = Object.values(serviceTableDataByFreelancer).some((freelancerData: any) => {
      const workingHours = freelancerData?.workingHours || [];
      return workingHours.some((wh: any) => {
        if (!ALL_VALIDATED_ARTICLE_NUMBERS.has(String(wh.articleNumber))) return false;
        return Number(wh.amount) === 0 || Number(wh.amountPerUnit) === 0;
      });
    });
    if (hasZeroAmountRow) {
      toast.error(String(t('WORKING_HOUR_AMOUNT_ZERO_ERROR') || 'One or more working hours have amount 0. Please enter a valid price before creating the invoice.'));
      setIsValidating(false);
      return;
    }

    // 2. Check if profile (city/country) is complete
    const isProfileComplete = await checkCompleteProfile();
    if (!isProfileComplete) {
      setIsValidating(false);
      return;
    }

    // 3. Check bank info
    const hasBankInfo = await checkBankInfo();
    if (!hasBankInfo) {
      setIsValidating(false);
      return;
    }

    // 4. Validate ROT deduction requirements
    const invoiceData = dataRef?.current;
    const preferences = invoiceData?.preferences || {};
    if (preferences.deductionType === 'rot') {
      const accommodationType = preferences.accommodationType;
      if (accommodationType === 'apartment') {
        if (!preferences.brfOrgNumber || !preferences.brfOrgNumber.trim()) {
          toast.error(String(t('ROT_REQUIRES_BRF_ORG_NUMBER')));
          setIsValidating(false);
          return;
        }
        if (!preferences.apartmentHouseNo || !preferences.apartmentHouseNo.trim()) {
          toast.error(String(t('ROT_REQUIRES_APARTMENT_HOUSE_NO')));
          setIsValidating(false);
          return;
        }
      } else if (accommodationType === 'house') {
        if (!preferences.property || !preferences.property.trim()) {
          toast.error(String(t('ROT_REQUIRES_PROPERTY')));
          setIsValidating(false);
          return;
        }
      }
    }

    // 5. Validate description
    if (!validateDescription()) {
      setIsValidating(false);
      return;
    }

    // Validate ROT/RUT requires at least one Payment Per Hour (article 92)
    const prefs = invoiceData?.preferences || {};
    if (prefs.deductionType === 'rot' || prefs.deductionType === 'rut') {
      const hasHourlyRow = Object.values(serviceTableDataByFreelancer).some((freelancerData: any) => {
        const workingHours = freelancerData?.workingHours || [];
        return workingHours.some((wh: any) => String(wh.articleNumber) === '92');
      });
      if (!hasHourlyRow) {
        toast.error(String(t('ROT_RUT_REQUIRES_PAYMENT_PER_HOUR') || 'When using ROT/RUT deduction, at least one working hour must be of type Payment Per Hour.'));
        return;
      }
    }


    // 6. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t('PLEASE_FILL_SERVICE_DESCRIPTION') || 'Please fill in service description for all working hours'));
          setIsValidating(false);
          return;
        }
      }
    }

    // 7. Validate dates: every working hour row must have a date
    if (validateDatesRef?.current) {
      const dateFunctions = validateDatesRef.current;
      const freelancerIds = Object.keys(dateFunctions);
      for (const freelancerId of freelancerIds) {
        const validateFunc = dateFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t('DATE_REQUIRED_ERROR') || 'Date is required for all rows'));
          setIsValidating(false);
          return;
        }
      }
    }

    // 8. All validations passed — show confirmation modal
    setIsValidating(false);
    setShowConfirmModal(true);
  };
  
  const handleConfirmSend = async () => {
    if (onSendInvoice) {
      const result = await onSendInvoice();
      // Return full result (could be boolean or API response object)
      // This allows the modal to extract isAlreadySynced if available
      return result;
    }
    return false;
  };
  
  const handleSuccess = () => {
    setShowConfirmModal(false);
    // Bypass unsaved-changes guard since invoice was just sent
    window.dispatchEvent(new CustomEvent('invoice-sent-successfully'));
    // Navigate to manage invoice page with current language
    const currentLanguage = i18n.language || 'en-us';
    navigate(`/v2/manage-invoice?lang=${currentLanguage}`);
  };
  
  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmModal(false);
    }
  };

  // Extract PDF data from response (matching old implementation pattern)
  // Reference: usePreliminaryInvoice.js
//   if (preliminaryResponse instanceof Blob) {
//         // The response is a blob from FileContentResult
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data instanceof Blob) {
//         // If response.data is a blob
//         pdfData = preliminaryResponse.data;
//       } else if (typeof preliminaryResponse === 'string') {
//         // Backwards compatibility for direct base64 response
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data && typeof preliminaryResponse.data === 'string') {
//         // Backwards compatibility for base64 strings in response.data
//         pdfData = preliminaryResponse.data;
//       } else if (preliminaryResponse?.pdfData) {
//         // Alternative property name
//         pdfData = preliminaryResponse.pdfData;
//       }
      
//       if (pdfData) {
//         setPdfData(pdfData);
//         return pdfData;
//       } else {
//         setError('No PDF data received from server');
//         return null;
//       }
 
  // preliminaryInvoice is now a string (object URL) from Redux
  const pdfUrl = preliminaryInvoice && typeof preliminaryInvoice === 'string'
    ? preliminaryInvoice
    : null;

  // We render the pages ourselves with react-pdf (fit-to-width) so the preview
  // displays fully and identically on every screen size, including mobile — no
  // reliance on the browser's native <iframe> PDF viewer (which showed at 50%
  // with scrollbars on desktop and failed to preview inline on mobile). The
  // toolbar's "Open in new tab" / "Download" remain as an escape hatch for very
  // large documents or users who prefer the OS viewer.

  if (!isOpen) return null;

  return (
    <div
      className="invoice-preview-page"
      ref={pageRootRef}
      style={availableHeight ? { height: availableHeight } : undefined}
    >
      <div className="invoice-preview-modal__container invoice-preview-modal__container--full">
        <div className="invoice-preview-modal__content invoice-preview-modal__content--full">

          <div className="invoice-preview-modal__info-row">
            {(() => {
              const calc = calculatedSubAmount?.response?.totalCalculation;
              if (!calc) return null;
              const rutAmt = calc.rutDeductionAmount || 0;
              const rotAmt = calc.rootDeductionAmount || 0;
              if (rutAmt <= 0 && rotAmt <= 0) return null;
              const customerPays = calc.amountIncludingVatAfterHouseholdServiceDeduction || 0;
              return (
                <div className="sidebar-deduction-breakdown">
                  <div className="deduction-row deduction-row--highlight">
                    <span className="deduction-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                    <span className="deduction-value deduction-value--primary">{formatAmountWithLocale(customerPays, 'SEK', i18n.language)}</span>
                  </div>
                  {rutAmt > 0 && (
                    <div className="deduction-row">
                      <div className="deduction-badge deduction-badge--rut">RUT</div>
                      <span className="deduction-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                      <span className="deduction-value deduction-value--deduct">-{formatAmountWithLocale(rutAmt, 'SEK', i18n.language)}</span>
                    </div>
                  )}
                  {rotAmt > 0 && (
                    <div className="deduction-row">
                      <div className="deduction-badge deduction-badge--rot">ROT</div>
                      <span className="deduction-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                      <span className="deduction-value deduction-value--deduct">-{formatAmountWithLocale(rotAmt, 'SEK', i18n.language)}</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="invoice-preview-modal__preview-wrapper">
            {preliminaryLoading ? (
              <div className="invoice-preview-modal__pdf-state">
                <span className="ipm-spinner" />
                <p>{t('LOADING')}</p>
              </div>
            ) : pdfUrl ? (
              <div className="invoice-preview-modal__viewer">
                {/* Clean, themed control bar (replaces the browser's native PDF toolbar) */}
                <div className="invoice-preview-modal__viewer-bar">
                  <div className="ipm-vb-left">
                    <span className="ipm-vb-heading">
                      <span className="ipm-vb-heading-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                          <path d="M11.67 1.67H5a1.67 1.67 0 0 0-1.67 1.66v13.34A1.67 1.67 0 0 0 5 18.33h10a1.67 1.67 0 0 0 1.67-1.66V6.67L11.67 1.67Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <path d="M11.67 1.67v5h5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      {t('PRELIMINARY_INVOICE_PREVIEW')}
                    </span>
                    <span className="ipm-vb-pages">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M11.67 1.67H5a1.67 1.67 0 0 0-1.67 1.66v13.34A1.67 1.67 0 0 0 5 18.33h10a1.67 1.67 0 0 0 1.67-1.66V6.67L11.67 1.67Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                        <path d="M11.67 1.67v5h5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                      </svg>
                      {numPages > 0 ? `${numPages} ${numPages === 1 ? (t('PAGE') || 'page') : (t('PAGES') || 'pages')}` : ''}
                    </span>
                  </div>

                  <div className="ipm-vb-zoom" role="group" aria-label={t('ZOOM') || 'Zoom'}>
                    <button type="button" onClick={zoomOut} disabled={zoom <= 0.5} aria-label={t('ZOOM_OUT') || 'Zoom out'}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M5 10h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    </button>
                    <button type="button" className="ipm-vb-zoom-val" onClick={resetZoom} aria-label={t('RESET_ZOOM') || 'Reset zoom'}>{Math.round(zoom * 100)}%</button>
                    <button type="button" onClick={zoomIn} disabled={zoom >= 2.5} aria-label={t('ZOOM_IN') || 'Zoom in'}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 5v10M5 10h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    </button>
                  </div>

                  <div className="ipm-vb-actions">
                    {allAttachedFiles.length > 0 && (
                      <button
                        type="button"
                        className="ipm-vb-template ipm-vb-attachments"
                        onClick={() => setShowAttachmentsModal(true)}
                      >
                        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M14.1 6.3l-6.2 6.2a2.3 2.3 0 0 0 3.2 3.3l6.2-6.2a3.8 3.8 0 1 0-5.4-5.4l-6.2 6.2a5.3 5.3 0 1 0 7.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t('ADDED_ATTACHMENT') || 'Added Attachment'}</span>
                        <span className="ipm-vb-attachments-count">{allAttachedFiles.length}</span>
                      </button>
                    )}
                    {/* Change invoice template (colors, logo size) — lives in the toolbar now */}
                    <a
                      className="ipm-vb-template"
                      href={`/profile-settings?tab=preferences&lang=${i18n.language || 'en-us'}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-tip={t('CHANGE_TEMPLATE_HINT') || 'Want a different look? Change your invoice template in Preferences.'}
                    >
                      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M14.06 4.94l1 1a1.5 1.5 0 0 1 0 2.12l-7.3 7.3-3.2.73.73-3.2 7.3-7.3a1.5 1.5 0 0 1 2.12 0l-.65.65Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{t('CHANGE_TEMPLATE') || 'Change template'}</span>
                    </a>
                    <a href={pdfUrl} download="invoice-preview.pdf" className="ipm-vb-btn" data-tip={t('DOWNLOAD') || 'Download'} aria-label={t('DOWNLOAD') || 'Download'}>
                      <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M10 3v9m0 0l-3.3-3.3M10 12l3.3-3.3M4 15v1.7A1.3 1.3 0 0 0 5.3 18h9.4a1.3 1.3 0 0 0 1.3-1.3V15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </a>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="ipm-vb-btn" data-tip={t('OPEN_IN_NEW_TAB') || 'Open in new tab'} aria-label={t('OPEN_IN_NEW_TAB') || 'Open in new tab'}>
                      <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M12 3h5v5M17 3l-7 7M8 4H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </a>
                    <button type="button" className="ipm-vb-btn ipm-vb-close" onClick={onClose} data-tip={t('CANCEL') || 'Close'} aria-label={t('CANCEL') || 'Close'}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                </div>

                {/* Scrollable, fit-to-width pages — renders identically on desktop & mobile */}
                <div
                  className="invoice-preview-modal__viewer-scroll"
                  ref={pdfScrollRef}
                  style={{ overflowX: zoom > 1 ? 'auto' : 'hidden' }}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }: { numPages: number }) => setNumPages(numPages)}
                    loading={<div className="invoice-preview-modal__pdf-state"><span className="ipm-spinner" /></div>}
                    error={<div className="invoice-preview-modal__pdf-state"><p>{t('NO_PDF_DATA_AVAILABLE')}</p></div>}
                  >
                    {Array.from({ length: numPages }).map((_, i) => (
                      <Page
                        key={i}
                        pageNumber={i + 1}
                        width={pageWidth ? pageWidth * zoom : undefined}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="invoice-preview-modal__pdf-page"
                        loading={<div className="invoice-preview-modal__pdf-state"><span className="ipm-spinner" /></div>}
                      />
                    ))}
                  </Document>
                </div>
              </div>
            ) : (
              <div className="invoice-preview-modal__pdf-state">
                <p>{t('NO_PDF_DATA_AVAILABLE')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <SendInvoiceConfirmationModal
        show={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSend}
        onSuccess={handleSuccess}
        isLoading={isLoading}
        isEditMode={isEditMode}
        invoiceAmount={totalAmount}
        invoiceDay={invoiceDay}
        dueDay={dueDay}
        calculatedSubAmount={calculatedSubAmount}
        selectedCustomer={selectedCustomer}
        customerIdFromQuery={customerIdFromQuery}
        preId={preId}
        dueDateFromEdit={dueDate}
      />

      <BankInfoModal
        show={showBankInfoModal}
        onClose={() => setShowBankInfoModal(false)}
      />
      <CompleteProfileModal
        show={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        onStartTour={() => setShowCompleteProfileModal(false)}
        isFromInvoice={true}
        initialCountry={initialProfileCountry}
      />

      {showAttachmentsModal && (
        <div className="ipm-attachments-modal-overlay" onClick={() => setShowAttachmentsModal(false)}>
          <div className="ipm-attachments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ipm-attachments-modal-header">
              <h3>{t('ADDED_ATTACHMENT') || 'Added Attachment'}</h3>
              <button type="button" className="ipm-attachments-modal-close" onClick={() => setShowAttachmentsModal(false)} aria-label={t('CANCEL') || 'Close'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <div className="attachment-list">
              {allAttachedFiles.map((fileItem, index) => (
                <div
                  key={index}
                  className="attachment-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const fileUrl = URL.createObjectURL(fileItem.file);
                    window.open(fileUrl, '_blank');
                  }}
                  title={t('CLICK_TO_VIEW_ATTACHMENT') || ''}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33333 2.89131 3.33333 3.33333V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="11.6667,1.66667 11.6667,6.66667 16.6667,6.66667" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{fileItem.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePreviewModal;
